import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import css from './GuestPicker.module.css';

/**
 * Shared guest-picker dropdown used in both the desktop SearchPageTopbar
 * and the mobile SearchFilterBar pill.
 *
 * @param {Function} props.onSearch           Called with updated URL param object on apply.
 * @param {string}   [props.className]        Extra class applied to the outer wrapper div.
 * @param {string}   [props.triggerClassName] Class for the trigger button (unselected state).
 * @param {string}   [props.triggerActiveClassName] Class added to trigger when guests are selected.
 * @param {boolean}  [props.sheetMode]        When true, opens as a bottom sheet instead of a dropdown.
 */
const GuestPicker = ({ onSearch, className, triggerClassName, triggerActiveClassName, sheetMode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [youngestAge, setYoungestAge] = useState(null); // null = not set
  const [pets, setPets] = useState(false);
  const containerRef = useRef(null);

  // Click-outside closes the dropdown (not needed in sheetMode — backdrop handles it)
  useEffect(() => {
    if (!isOpen || sheetMode) return;
    const handle = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [isOpen, sheetMode]);

  const AGE_STEPS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 16, 18, 21];

  const isDefault = adults === 1 && children === 0 && infants === 0 && youngestAge === null && !pets;
  const total = adults + children + infants;

  const buttonLabel = (() => {
    if (isDefault) return 'Who';
    const parts = [];
    if (total === 1 && !pets && youngestAge === null) {
      parts.push('1 adult');
    } else if (total > 1) {
      parts.push(`${total} guests`);
    } else {
      parts.push('Guests');
    }
    if (youngestAge !== null) parts.push(`age ${youngestAge}+`);
    if (pets) parts.push('🐾');
    return parts.join(' · ');
  })();

  const handleClear = () => {
    setAdults(1);
    setChildren(0);
    setInfants(0);
    setYoungestAge(null);
    setPets(false);
  };

  const handleApply = () => {
    if (isDefault) {
      onSearch({
        pub_passenger_capacity: null,
        pub_minimum_participant_age: null,
        pub_boat_activities_allowed: null,
      });
    } else {
      onSearch({
        pub_passenger_capacity: `${total},2147483647`,
        pub_minimum_participant_age: youngestAge !== null ? `0,${youngestAge + 1}` : null,
        pub_boat_activities_allowed: pets ? 'has_any:pets-allowed' : null,
      });
    }
    setIsOpen(false);
  };

  const Stepper = ({ label, sublabel, value, setValue, min, max }) => (
    <div className={css.guestRow}>
      <div className={css.guestRowLabel}>
        <span className={css.guestRowTitle}>{label}</span>
        {sublabel && <span className={css.guestRowSub}>{sublabel}</span>}
      </div>
      <div className={css.guestStepper}>
        <button
          type="button"
          className={css.stepperBtn}
          onClick={() => setValue(Math.max(min, value - 1))}
          disabled={value <= min}
        >
          −
        </button>
        <span className={css.stepperCount}>{value}</span>
        <button
          type="button"
          className={css.stepperBtn}
          onClick={() => setValue(Math.min(max, value + 1))}
          disabled={value >= max}
        >
          +
        </button>
      </div>
    </div>
  );

  const resolvedTriggerClass = triggerClassName || css.trigger;
  const resolvedTriggerActiveClass = triggerActiveClassName || css.triggerActive;

  return (
    <div className={classNames(css.wrapper, className)} ref={containerRef}>
      <button
        type="button"
        className={classNames(resolvedTriggerClass, { [resolvedTriggerActiveClass]: !isDefault })}
        onClick={() => setIsOpen(o => !o)}
      >
        {buttonLabel}
      </button>
      {isOpen && !sheetMode && (
        <div className={css.dropdown}>
          <Stepper label="Adults" sublabel="Age 13+" value={adults} setValue={setAdults} min={1} max={20} />
          <Stepper label="Children" sublabel="Ages 3–12" value={children} setValue={setChildren} min={0} max={20} />
          <Stepper label="Infants" sublabel="Under 3" value={infants} setValue={setInfants} min={0} max={10} />
          <div className={css.guestRow}>
            <div className={css.guestRowLabel}>
              <span className={css.guestRowTitle}>Min. Age</span>
              <span className={css.guestRowSub}>0–21</span>
            </div>
            <div className={css.guestStepper}>
              <button type="button" className={css.stepperBtn} onClick={() => { const idx = AGE_STEPS.indexOf(youngestAge); setYoungestAge(idx > 0 ? AGE_STEPS[idx - 1] : AGE_STEPS[0]); }} disabled={youngestAge === null || youngestAge === AGE_STEPS[0]}>−</button>
              <span className={css.stepperCount}>{youngestAge === null ? '—' : youngestAge}</span>
              <button type="button" className={css.stepperBtn} onClick={() => { const idx = AGE_STEPS.indexOf(youngestAge); setYoungestAge(idx === -1 ? AGE_STEPS[0] : AGE_STEPS[idx + 1]); }} disabled={youngestAge === AGE_STEPS[AGE_STEPS.length - 1]}>+</button>
            </div>
          </div>
          <div className={css.guestRow}>
            <div className={css.guestRowLabel}><span className={css.guestRowTitle}>Pets</span></div>
            <div className={css.petsOptions}>
              <button type="button" className={classNames(css.petsOption, { [css.petsOptionActive]: pets })} onClick={() => setPets(true)}>Yes</button>
              <button type="button" className={classNames(css.petsOption, { [css.petsOptionActive]: !pets })} onClick={() => setPets(false)}>No</button>
            </div>
          </div>
          <div className={css.guestFooter}>
            <button type="button" className={css.guestClear} onClick={handleClear}>Clear</button>
            <button type="button" className={css.guestApply} onClick={handleApply}>Apply</button>
          </div>
        </div>
      )}
      {isOpen && sheetMode && (
        <>
          <div className={css.sheetBackdrop} onClick={() => setIsOpen(false)} />
          <div className={css.guestSheet} role="dialog" aria-label="Select guests">
            <div className={css.sheetHandle} />
            <div className={css.sheetTitleRow}>
              <span className={css.sheetTitle}>Guests</span>
              <button type="button" className={css.sheetCloseBtn} onClick={() => setIsOpen(false)} aria-label="Close">✕</button>
            </div>
            <div className={css.sheetBody}>
              <Stepper label="Adults" sublabel="Age 13+" value={adults} setValue={setAdults} min={1} max={20} />
              <Stepper label="Children" sublabel="Ages 3–12" value={children} setValue={setChildren} min={0} max={20} />
              <Stepper label="Infants" sublabel="Under 3" value={infants} setValue={setInfants} min={0} max={10} />
              <div className={css.guestRow}>
                <div className={css.guestRowLabel}>
                  <span className={css.guestRowTitle}>Min. Age</span>
                  <span className={css.guestRowSub}>0–21</span>
                </div>
                <div className={css.guestStepper}>
                  <button type="button" className={css.stepperBtn} onClick={() => { const idx = AGE_STEPS.indexOf(youngestAge); setYoungestAge(idx > 0 ? AGE_STEPS[idx - 1] : AGE_STEPS[0]); }} disabled={youngestAge === null || youngestAge === AGE_STEPS[0]}>−</button>
                  <span className={css.stepperCount}>{youngestAge === null ? '—' : youngestAge}</span>
                  <button type="button" className={css.stepperBtn} onClick={() => { const idx = AGE_STEPS.indexOf(youngestAge); setYoungestAge(idx === -1 ? AGE_STEPS[0] : AGE_STEPS[idx + 1]); }} disabled={youngestAge === AGE_STEPS[AGE_STEPS.length - 1]}>+</button>
                </div>
              </div>
              <div className={css.guestRow}>
                <div className={css.guestRowLabel}><span className={css.guestRowTitle}>Pets</span></div>
                <div className={css.petsOptions}>
                  <button type="button" className={classNames(css.petsOption, { [css.petsOptionActive]: pets })} onClick={() => setPets(true)}>Yes</button>
                  <button type="button" className={classNames(css.petsOption, { [css.petsOptionActive]: !pets })} onClick={() => setPets(false)}>No</button>
                </div>
              </div>
            </div>
            <div className={css.guestFooter}>
              <button type="button" className={css.guestClear} onClick={handleClear}>Clear</button>
              <button type="button" className={css.guestApply} onClick={handleApply}>Apply</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GuestPicker;
